import { Link, LinkProps } from '@tanstack/react-router';
import classNames from 'classnames';
import { MouseEventHandler, ReactElement, ReactNode, useId } from 'react';
import { Tooltip } from 'react-tooltip';
import { Loader } from './Loader';

type HeroIconType =
  | React.ForwardRefExoticComponent<
      Omit<React.SVGProps<SVGSVGElement>, 'ref'> &
        React.RefAttributes<SVGSVGElement> & {
          title?: string;
          titleId?: string;
        }
    >
  | React.FunctionComponent<React.SVGProps<SVGSVGElement>>;

const sizeMap = {
  xl: { x: 24, y: 16 },
  large: { x: 20, y: 12 },
  medium: { x: 16, y: 8 },
  small: { x: 12, y: 8 },
};

const typeMap = {
  basic: 'active:shadow-focused-dark bg-grey-t-900 hover:bg-grey-t-800',
  simple:
    'active:shadow-focused-dark hover:bg-grey-t-800 text-text-low-priority',
  primary:
    'active:shadow-focused bg-primary-400 hover:bg-primary-300 text-text-filled-component',
  'low-priority':
    'active:shadow-focused-dark hover:bg-grey-t-900 border-solid! border border-grey-t-700 group-data-[status=active]:border-none group-disabled:border-background-disabled',
  tertiary:
    'active:shadow-focused bg-none text-primary-400 hover:bg-grey-t-900',
  secondary: `active:shadow-focused-dark bg-grey-200 text-text-filled-component hover:bg-grey-100`,
  delete:
    'active:shadow-focused-dark border border-grey-t-600 text-red-400 group-data-[status=active]:border-none group-disabled:border-background-disabled',
};

interface Props {
  button: ButtonProps;
  link?: LinkProps | undefined;
}

export type ButtonComponentPropsType = Props;

interface ButtonProps {
  onClick?:
    | MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
    | undefined;
  text: string;
  disabled?: boolean | undefined;
  IconStart?: HeroIconType | undefined;
  IconEnd?: HeroIconType | undefined;
  loading?: boolean | undefined;
  type?: keyof typeof typeMap | undefined;
  size?: keyof typeof sizeMap | undefined;
  isSubmit?: boolean | undefined;
  showActiveButton?: boolean | undefined;
  shrink?: boolean | undefined;
  tooltip?: string | undefined;
}

export const getButtonClassnames = ({
  type,
}: {
  type: NonNullable<ButtonProps['type']>;
}) =>
  `justify-center rounded-xs ${typeMap[type]} group-disabled:text-text-disabled group-disabled:bg-background-disabled flex items-center space-x-2 group-disabled:active:shadow-none  group-disabled:hover:bg-background-disabled`;

const getButtonTextSize = ({
  size,
}: {
  size: NonNullable<ButtonProps['size']>;
}) =>
  ({
    large: 'text-[0.9375rem] leading-6',
    xl: 'text-[17px] leading-6',
    small: 'text-sm',
    medium: 'text-[0.9375rem] leading-6',
  }[size]);

export const getButtonStyles = ({
  size,
  type,
  additionalClassnames = '',
}: {
  size: NonNullable<ButtonProps['size']>;
  type: NonNullable<ButtonProps['type']>;
  additionalClassnames?: string;
}) => {
  const className = `${getButtonClassnames({
    type,
  })} font-bold whitespace-nowrap ${getButtonTextSize({
    size,
  })} ${additionalClassnames}`;

  const { x, y } = sizeMap[size];

  const isBordered = className.includes('border');

  return {
    style: {
      padding: `${y - (isBordered ? 1 : 0)}px ${x - (isBordered ? 1 : 0)}px`,
    },
    className,
  };
};
const ButtonInner = ({
  text,
  type = 'primary',
  size = 'medium',
  IconStart,
  IconEnd,
  showActiveButton,
  loading,
}: Omit<ButtonProps, 'disabled' | 'isSubmit' | 'onClick'>) => {
  const { style, className: cs } = getButtonStyles({ type, size });

  return (
    <div
      className={classNames(cs, {
        'group-data-[status=active]:bg-grey-t-800 group-data-[status=active]:text-black':
          showActiveButton,
      })}
      style={style}
    >
      {IconStart && <IconStart className="size-5" />}
      <div className="flex items-center relative">
        {text}{' '}
        {loading && (
          <Loader
            fill={`#${type === 'secondary' ? '000000' : 'ffffff'}4d`}
            className="size-4 absolute -right-8"
          />
        )}
      </div>
      {IconEnd && !loading && <IconEnd className="size-5" />}
    </div>
  );
};

const ButtonButton = ({
  disabled,
  onClick,
  isSubmit,
  shrink,
  tooltip,
  ...rest
}: ButtonProps) => {
  const tooltipId = useId();

  return (
    <>
      <button
        disabled={disabled}
        type={isSubmit ? 'submit' : 'button'}
        onClick={onClick}
        className={classNames('group', {
          'w-full': !shrink,
          'w-fit': shrink,
        })}
        {...(tooltip
          ? {
              'data-tooltip-id': tooltip,
              'data-tooltip-content': tooltip,
            }
          : {})}
      >
        <ButtonInner {...rest} />
      </button>
      {tooltip && <Tooltip id={tooltipId} />}
    </>
  );
};

const DynamicLink = ({
  children,
  link,
  onClick,
  shrink,
}: {
  children: ReactNode;
  link: LinkProps;
  shrink?: boolean | undefined;
  onClick?: Props['button']['onClick'];
}) =>
  link.href ? (
    <a
      onClick={onClick}
      href={link.href}
      rel="noreferrer noopener"
      target={link.target}
      className={classNames({
        'w-full': !shrink,
        'w-fit': shrink,
      })}
    >
      {children}
    </a>
  ) : (
    <Link
      className={classNames('block group', {
        'w-full': !shrink,
        'w-fit': shrink,
      })}
      onClick={onClick}
      {...link}
    >
      {children}
    </Link>
  );

export const Button = ({ button, link }: Props): ReactElement =>
  link && !button.disabled ? (
    <DynamicLink shrink={button.shrink} onClick={button.onClick} link={link}>
      <ButtonInner {...button} />
    </DynamicLink>
  ) : (
    <ButtonButton {...button} />
  );
